import { t } from '@viral-space/shared';

const JOYSTICK_RADIUS = 60;
const JOYSTICK_MAX_OFFSET = 50;
const DEADZONE = 0.12;

// Vertical anchor for the proximity prompt stack (DOCK / WARP / TRADE).
// History: 210 → 95 → 160 (with min floor). 210 parked the pill mid-
// screen on top of the player ship; 95 fixed that but on a landscape
// iPhone the bottom edge (≈scalePx(95) = 47px + safe-area-inset-bottom)
// landed right on the "SAFE ZONE / SPEED / COORDS" strip (`bottom: 16px`
// + ~55px of fixed-font content → top edge at ~71px), so the pill
// almost touched the SAFE ZONE label. We now anchor at scalePx(160)
// AND clamp to a minimum 92px floor (see PROMPT_STACK_FLOOR) so even on
// short landscape phones (vmin ≈ 400 → scalePx(160)=80) the pill ends
// up safely above the bottom HUD. PROMPT_STACK_GAP is similarly
// floored so stacking DOCK + WARP doesn't squash them into each other
// on the same small phones (scaled-down 50 → 25 < button height).
const PROMPT_STACK_BASE = 160;
const PROMPT_STACK_FLOOR = 92;
const PROMPT_STACK_GAP = 50;
const PROMPT_STACK_GAP_FLOOR = 44;

function promptBottomCss(level: number): string {
  // Returns a CSS `bottom` value for stack `level` (0 = nearest to the
  // bottom HUD). Uses CSS `max()` so the floor wins on small phones
  // where scalePx shrinks the raw value, and the raw value wins on
  // tablets/desktops where it scales up.
  const baseScaled = scalePx(PROMPT_STACK_BASE);
  const gapScaled = scalePx(PROMPT_STACK_GAP);
  const baseExpr = `max(${baseScaled}px, ${PROMPT_STACK_FLOOR}px)`;
  if (level <= 0) {
    return `calc(${baseExpr} + env(safe-area-inset-bottom, 0px))`;
  }
  const gapExpr = `max(${gapScaled}px, ${PROMPT_STACK_GAP_FLOOR}px)`;
  return `calc(${baseExpr} + ${level} * ${gapExpr} + env(safe-area-inset-bottom, 0px))`;
}

const WIRE_GREEN = '#00ff41';
const WIRE_GREEN_DIM = 'rgba(0,255,65,0.25)';
const WIRE_GREEN_GLOW = 'rgba(0,255,65,0.5)';
const WIRE_BG = 'rgba(0,16,0,0.3)';

/**
 * Inline-SVG icons for the top launcher bar. Hand-rolled (instead of
 * pulling in a font icon library) so:
 *   - There's zero added dependency / network cost — the markup ships
 *     inside the same JS bundle that already builds the overlay.
 *   - The strokes use `currentColor`, so the wire-green palette and
 *     the `filter: drop-shadow` glow in `makeIconButton` both flow
 *     through without per-icon CSS.
 *   - We're free of system-font lottery: the previous Unicode glyphs
 *     (▦ ☻ ◉ ✦) rendered with wildly different stroke weights across
 *     iOS Safari, Android stock, and desktop Chromium, with the
 *     inventory grid in particular drawing as "barely visible" hair-
 *     line strokes on some Android builds.
 *
 * Each viewBox is 24×24 with a 22×22 visual area (1px of padding) so
 * the icons share an apparent stroke weight even though individual
 * shapes have different bounding boxes.
 */
const ICON_STROKE = 'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" fill="none"';

/** Hamburger menu — three horizontal lines, the universal "open menu". */
const ICON_MENU = `
<svg viewBox="0 0 24 24" ${ICON_STROKE}>
  <line x1="4" y1="7" x2="20" y2="7"/>
  <line x1="4" y1="12" x2="20" y2="12"/>
  <line x1="4" y1="17" x2="20" y2="17"/>
</svg>`;

/**
 * Globe — circle + a latitude ellipse + an equator line + two meridian
 * arcs. Reads unambiguously as a "world map" affordance, which matches
 * the universe-map this button opens (sectors / star systems / planets).
 * The previous concentric-circles glyph (◉) was being mistaken for a
 * radar / target reticle by playtesters.
 */
const ICON_GLOBE = `
<svg viewBox="0 0 24 24" ${ICON_STROKE}>
  <circle cx="12" cy="12" r="9"/>
  <ellipse cx="12" cy="12" rx="9" ry="3.6"/>
  <line x1="3" y1="12" x2="21" y2="12"/>
  <path d="M12 3 C 7.5 7, 7.5 17, 12 21"/>
  <path d="M12 3 C 16.5 7, 16.5 17, 12 21"/>
</svg>`;

/**
 * Clipboard with a checkmark — universally read as "tasks / objectives".
 * Replaces the sparkle (✦) glyph, which carried no semantic link to
 * "missions" and confused players the user explicitly called out.
 */
const ICON_CLIPBOARD = `
<svg viewBox="0 0 24 24" ${ICON_STROKE}>
  <rect x="5" y="4.5" width="14" height="16.5" rx="1.5"/>
  <rect x="9" y="2.8" width="6" height="3.4" rx="0.7"/>
  <path d="M8 13.5 L11 16.5 L16.5 10.5"/>
</svg>`;

/**
 * Backpack / satchel — a clearer "inventory" affordance than the 2×2
 * grid glyph (▦), which the user reported as "barely visible" on
 * mobile. The handle hump + main body silhouette reads as a carry-
 * bag even at 22px and shares no visual vocabulary with the other
 * four icons (so the bar stays scannable).
 */
const ICON_BACKPACK = `
<svg viewBox="0 0 24 24" ${ICON_STROKE}>
  <path d="M5.5 9.5 L5.5 19.5 A1.5 1.5 0 0 0 7 21 L17 21 A1.5 1.5 0 0 0 18.5 19.5 L18.5 9.5 Z"/>
  <path d="M8.5 9.5 L8.5 7 A3.5 3.5 0 0 1 15.5 7 L15.5 9.5"/>
  <line x1="5.5" y1="14" x2="18.5" y2="14"/>
  <rect x="10" y="15.4" width="4" height="2.4" rx="0.4"/>
</svg>`;

/**
 * Two-people "users" icon — replaces the smiley face (☻) the user
 * also called out. Front + back silhouettes (slight offset) reads
 * unambiguously as "friends list" on any mobile size.
 */
const ICON_USERS = `
<svg viewBox="0 0 24 24" ${ICON_STROKE}>
  <circle cx="9" cy="9" r="3.2"/>
  <path d="M3.5 19 C 4 15, 14 15, 14.5 19"/>
  <circle cx="16.5" cy="8.5" r="2.6"/>
  <path d="M14.5 13.6 C 16 13, 21 13.5, 20.5 18"/>
</svg>`;

// The flight sticks deliberately use a desaturated, low-opacity palette
// so they read as transparent overlays instead of competing with the
// bright green HUD widgets (radar, weapon panels, hull/shield bars)
// they sit on top of in landscape.
const STICK_BASE_BG = 'rgba(255,255,255,0.04)';
const STICK_BASE_BORDER = 'rgba(255,255,255,0.18)';
const STICK_THUMB_BG = 'rgba(255,255,255,0.10)';
const STICK_THUMB_BG_ACTIVE = 'rgba(255,255,255,0.22)';
const STICK_THUMB_BORDER = 'rgba(255,255,255,0.45)';
const STICK_LABEL_COLOR = 'rgba(255,255,255,0.35)';

function scalePx(base: number): number {
  const vmin = Math.min(window.innerWidth, window.innerHeight);
  return Math.round(base * (vmin / 800));
}

interface JoystickState {
  active: boolean;
  identifier: number;
  originX: number;
  originY: number;
  currentX: number;
  currentY: number;
  dx: number;
  dy: number;
}

/**
 * Callback bag wired up by `main.ts`. Each callback is optional so the
 * overlay works even before a character is loaded (e.g. the menu button
 * is still useful during the character select phase). Unset callbacks
 * simply hide the associated button.
 */
export interface TouchControlsCallbacks {
  onDock?: () => void;
  onUndock?: () => void;
  onWeaponCycle?: () => void;
  onInventory?: () => void;
  onMap?: () => void;
  onMenu?: () => void;
  onChat?: () => void;
  onFriends?: () => void;
  /**
   * Tapped when the pilot opens the mission journal pill on the top
   * action bar. `main.ts` toggles the same journal that desktop opens
   * with `J`, so the mobile path mirrors keyboard parity.
   */
  onMissions?: () => void;
  onWarp?: () => void;
  onTrade?: () => void;
  onUse?: () => void;
  /**
   * Fired when the pilot taps the on-screen LAND / LIFTOFF prompt
   * (proximity-driven, see `showLand`). `main.ts` routes the tap to
   * `sendLandRequest` while in orbit and `sendLiftoffRequest` while
   * on a surface — the desktop `P` key uses the same dispatch.
   */
  onLand?: () => void;
  /**
   * Fired when the pilot toggles the auto-fire state via the fire/auto
   * pill. `enabled=true` means the touch overlay now wants the game to
   * automatically fire whenever a crosshair target is in weapon range;
   * `false` clears that state. `main.ts` uses this to surface a HUD
   * notification so the new pill label isn't a mystery.
   */
  onToggleAutoFire?: (enabled: boolean) => void;
  /**
   * Fired when the pilot toggles the boost state via the boost pill.
   * `enabled=true` means boost is armed and will engage automatically
   * whenever the thrust stick (or up/down pills) is pushed; `false`
   * clears that state. `main.ts` uses this to show a HUD notification
   * so the pilot knows why their ship is suddenly faster (or isn't).
   */
  onToggleBoost?: (enabled: boolean) => void;
}

export class TouchControls {
  private container: HTMLElement;
  private root: HTMLElement;

  private leftJoystick: JoystickState = { active: false, identifier: -1, originX: 0, originY: 0, currentX: 0, currentY: 0, dx: 0, dy: 0 };
  private rightJoystick: JoystickState = { active: false, identifier: -1, originX: 0, originY: 0, currentX: 0, currentY: 0, dx: 0, dy: 0 };
  /**
   * Mobile fire is a *toggle*, not a press-and-hold: tapping the pill
   * flips `_autoFireEnabled`, which makes the pill show "AUTO" and arms
   * the auto-fire system. Actual trigger-pulling is driven by
   * `_autoFireActive`, which `main.ts` sets every frame only when a
   * valid target (hostile ship/NPC or asteroid) is in the crosshair AND
   * inside the equipped weapon's range. The input state fed to the
   * server therefore only sees `isFiring=true` when BOTH flags are
   * true, so the server still thinks the pilot is manually pulling the
   * trigger — no server-side changes needed for auto-fire.
   */
  private _autoFireEnabled = false;
  private _autoFireActive = false;
  /**
   * Boost is also a toggle on mobile: tapping the pill arms boost
   * (`_boostEnabled`), but the input state only reports boosting when
   * the pilot is actually pushing the stick (see `hasThrustInput()`).
   * That way leaving boost armed while idle doesn't silently drain
   * energy/fuel, and the pilot can tap once at the start of a long
   * run instead of press-and-hold with a second thumb.
   */
  private _boostEnabled = false;
  private _upHeld = false;
  private _downHeld = false;
  private upIdentifier = -1;
  private downIdentifier = -1;

  private leftBase!: HTMLElement;
  private leftThumb!: HTMLElement;
  private rightBase!: HTMLElement;
  private rightThumb!: HTMLElement;
  private fireBtn!: HTMLElement;
  private fireBtnLabel!: HTMLElement;
  private boostBtnLabel!: HTMLElement;
  /**
   * Latched copy of the last computed "is the pilot currently pushing
   * the throttle in any direction" result, used purely to drive the
   * BOOST pill's "armed vs burning" visual. Updated from
   * `hasThrustInput()` the moment we compute it (once per frame from
   * `getThrust()` / `isBoosting()`), so the visual stays in lockstep
   * with the input state the input-manager actually reads.
   */
  private _lastThrusting = false;
  private boostBtn!: HTMLElement;
  private upBtn!: HTMLElement;
  private downBtn!: HTMLElement;
  private dockBtn!: HTMLElement;
  private weaponCycleBtn!: HTMLElement;
  private warpBtn!: HTMLElement;
  private tradeBtn!: HTMLElement;
  private landBtn!: HTMLElement;
  private inventoryBtn!: HTMLElement;
  private mapBtn!: HTMLElement;
  private menuBtn!: HTMLElement;
  private missionsBtn!: HTMLElement;
  private chatBtn!: HTMLElement;
  private useBtn!: HTMLElement;
  private friendsBtn!: HTMLElement;
  private topBar!: HTMLElement;

  private dockVisible = false;
  private warpVisible = false;
  private tradeVisible = false;
  private landVisible = false;

  private cb: TouchControlsCallbacks = {};

  private disposed = false;
  private visible = false;

  private readonly onTouchStart: (e: TouchEvent) => void;
  private readonly onTouchMove: (e: TouchEvent) => void;
  private readonly onTouchEnd: (e: TouchEvent) => void;
  private readonly onResize: () => void;

  constructor(container: HTMLElement) {
    this.container = container;

    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '20',
      display: 'none',
      fontFamily: "'Share Tech Mono', monospace",
      userSelect: 'none',
      webkitUserSelect: 'none',
      touchAction: 'none',
    } as Partial<CSSStyleDeclaration>);
    this.container.appendChild(this.root);

    this.buildLeftJoystick();
    this.buildRightJoystick();
    this.buildFireButton();
    this.buildBoostButton();
    this.buildVerticalThrustButtons();
    this.buildDockButton();
    this.buildWarpButton();
    this.buildTradeButton();
    this.buildLandButton();
    this.buildTopButtonBar();

    this.onTouchStart = this.handleTouchStart.bind(this);
    this.onTouchMove = this.handleTouchMove.bind(this);
    this.onTouchEnd = this.handleTouchEnd.bind(this);
    this.onResize = this.handleResize.bind(this);

    this.root.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.root.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.root.addEventListener('touchend', this.onTouchEnd);
    this.root.addEventListener('touchcancel', this.onTouchEnd);
    window.addEventListener('resize', this.onResize);
  }

  setCallbacks(callbacks: TouchControlsCallbacks): void {
    this.cb = { ...this.cb, ...callbacks };
  }

  private makeCircle(
    radius: number,
    border: string,
    bg: string,
    extra?: Partial<CSSStyleDeclaration>,
  ): HTMLElement {
    const el = document.createElement('div');
    const d = radius * 2;
    Object.assign(el.style, {
      position: 'absolute',
      width: `${d}px`,
      height: `${d}px`,
      borderRadius: '50%',
      border,
      background: bg,
      boxSizing: 'border-box',
      pointerEvents: 'auto',
      touchAction: 'none',
      ...extra,
    } as Partial<CSSStyleDeclaration>);
    return el;
  }

  /**
   * Square icon-only button used by the top navigation bar (MENU /
   * MAP / MIS / INV / FRI). Replaces the previous text pills so the
   * bar reads as a launcher of glanceable icons rather than a row of
   * tiny three-letter abbreviations. The `aria-label` carries the
   * full localized label for screen readers, and `title` populates a
   * long-press tooltip on devices that surface it.
   *
   * `icon` is interpreted two ways:
   *   - If it starts with `<svg`, the contents are mounted as inline
   *     SVG markup (lets us hand-roll crisp line-art glyphs that
   *     render identically across iOS/Android/desktop without relying
   *     on a system font's particular drawing of an obscure Unicode
   *     character — the previous ☰ / ◉ / ✦ / ▦ / ☻ approach drew
   *     "barely visible" on some Android stocks). All strokes default
   *     to `currentColor` so the wire-green palette flows through.
   *   - Otherwise it's treated as a text glyph (legacy path; still
   *     used by any future button that wants a single character).
   *
   * The icon is rendered via a child `<span>` (instead of being the
   * button's text) so the parent retains a stable hit box even if a
   * future glyph turns out to be wider/narrower than the others.
   */
  private makeIconButton(
    icon: string,
    accessibleLabel: string,
    extra?: Partial<CSSStyleDeclaration>,
  ): HTMLElement {
    const btn = document.createElement('div');
    const size = Math.max(scalePx(34), 38);
    Object.assign(btn.style, {
      position: 'absolute',
      width: `${size}px`,
      height: `${size}px`,
      border: `1px solid ${WIRE_GREEN_DIM}`,
      background: WIRE_BG,
      boxShadow: `0 0 6px ${WIRE_GREEN_DIM}`,
      color: WIRE_GREEN,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'auto',
      touchAction: 'none',
      borderRadius: '6px',
      ...extra,
    } as Partial<CSSStyleDeclaration>);
    btn.setAttribute('aria-label', accessibleLabel);
    btn.title = accessibleLabel;

    const iconEl = document.createElement('span');
    const iconPx = Math.max(20, scalePx(22));
    const isSvg = icon.trimStart().startsWith('<svg');
    Object.assign(iconEl.style, {
      // `display: inline-flex` keeps the icon visually centered in
      // the button even when the SVG carries internal padding inside
      // its viewBox.
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: `${iconPx}px`,
      height: `${iconPx}px`,
      fontSize: `${Math.max(16, scalePx(18))}px`,
      lineHeight: '1',
      pointerEvents: 'none',
      // `filter: drop-shadow` works on both inline SVG and text and
      // gives the same wire-HUD halo the old `text-shadow` produced
      // for glyphs (text-shadow doesn't reach SVG content).
      filter: `drop-shadow(0 0 5px ${WIRE_GREEN_GLOW})`,
    } as Partial<CSSStyleDeclaration>);
    if (isSvg) {
      iconEl.innerHTML = icon;
      const svg = iconEl.firstElementChild as SVGSVGElement | null;
      if (svg) {
        svg.setAttribute('width', '100%');
        svg.setAttribute('height', '100%');
        // Force the icon's strokes to follow the button's `color` so
        // a future palette change (e.g. a hostile-state red bar)
        // doesn't have to touch every SVG.
        svg.style.color = 'currentColor';
        svg.style.display = 'block';
      }
    } else {
      iconEl.textContent = icon;
    }
    btn.appendChild(iconEl);

    return btn;
  }

  private buildLeftJoystick(): void {
    const r = scalePx(JOYSTICK_RADIUS);
    // Lifted ~25px higher and pushed ~25px toward screen-center so the
    // joystick base doesn't crowd the BOOST button (which sits at
    // bottom:18 left:22 with r=40 — previously its outer edge was only
    // 2px away from the stick base). The thumb zone now has a clear
    // vertical gap above the trigger buttons and a bit more breathing
    // room between the stick and the screen edge. Offsets are kept
    // modest so they don't collide with the enlarged mobile radar on
    // small landscape phones.
    this.leftBase = this.makeCircle(r, `1px solid ${STICK_BASE_BORDER}`, STICK_BASE_BG, {
      bottom: `calc(${scalePx(125)}px + env(safe-area-inset-bottom, 0px))`,
      left: `calc(${scalePx(65)}px + env(safe-area-inset-left, 0px))`,
      boxShadow: 'none',
    });

    const innerR = scalePx(22);
    this.leftThumb = this.makeCircle(innerR, `1px solid ${STICK_THUMB_BORDER}`, STICK_THUMB_BG, {
      left: `${r - innerR}px`,
      top: `${r - innerR}px`,
      boxShadow: 'none',
      transition: 'left 0.05s, top 0.05s, background 0.1s',
    });
    this.leftBase.appendChild(this.leftThumb);
    this.root.appendChild(this.leftBase);
  }

  private buildRightJoystick(): void {
    const r = scalePx(JOYSTICK_RADIUS);
    // Mirrors the LEFT joystick offset (see there for rationale) so the
    // AIM stick clears the FIRE button and gets a bit more margin from
    // the screen edge on landscape phones.
    this.rightBase = this.makeCircle(r, `1px solid ${STICK_BASE_BORDER}`, STICK_BASE_BG, {
      bottom: `calc(${scalePx(125)}px + env(safe-area-inset-bottom, 0px))`,
      right: `calc(${scalePx(65)}px + env(safe-area-inset-right, 0px))`,
      boxShadow: 'none',
    });

    const innerR = scalePx(22);
    this.rightThumb = this.makeCircle(innerR, `1px solid ${STICK_THUMB_BORDER}`, STICK_THUMB_BG, {
      left: `${r - innerR}px`,
      top: `${r - innerR}px`,
      boxShadow: 'none',
      transition: 'left 0.05s, top 0.05s, background 0.1s',
    });
    this.rightBase.appendChild(this.rightThumb);
    this.root.appendChild(this.rightBase);
  }

  private buildFireButton(): void {
    const r = scalePx(40);
    this.fireBtn = this.makeCircle(r, `2px solid ${WIRE_GREEN}`, 'rgba(0,255,65,0.1)', {
      bottom: `calc(${scalePx(18)}px + env(safe-area-inset-bottom, 0px))`,
      right: `calc(${scalePx(22)}px + env(safe-area-inset-right, 0px))`,
      boxShadow: `0 0 10px ${WIRE_GREEN_DIM}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });

    const inner = document.createElement('span');
    Object.assign(inner.style, {
      color: WIRE_GREEN,
      fontSize: `${scalePx(10)}px`,
      letterSpacing: '1px',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);
    inner.textContent = t('touch.fire');
    this.fireBtn.appendChild(inner);
    this.fireBtnLabel = inner;

    this.root.appendChild(this.fireBtn);
  }

  /**
   * Sync the pill's label + glow to the current `_autoFireEnabled`
   * flag. Kept as a single helper so the touchdown handler and the
   * hide()/reset paths can't disagree about the visual state.
   */
  private applyFireButtonStyle(): void {
    if (this._autoFireEnabled) {
      this.fireBtnLabel.textContent = t('touch.auto');
      this.fireBtnLabel.style.color = '#ff7733';
      this.fireBtn.style.border = '2px solid #ff7733';
      this.fireBtn.style.boxShadow = '0 0 20px rgba(255,120,40,0.85), inset 0 0 15px rgba(255,120,40,0.3)';
      this.fireBtn.style.background = 'rgba(255,120,40,0.3)';
    } else {
      this.fireBtnLabel.textContent = t('touch.fire');
      this.fireBtnLabel.style.color = WIRE_GREEN;
      this.fireBtn.style.border = `2px solid ${WIRE_GREEN}`;
      this.fireBtn.style.boxShadow = `0 0 10px ${WIRE_GREEN_DIM}`;
      this.fireBtn.style.background = 'rgba(0,255,65,0.1)';
    }
  }

  private buildBoostButton(): void {
    const r = scalePx(40);
    this.boostBtn = this.makeCircle(r, `2px solid ${WIRE_GREEN}`, 'rgba(0,255,65,0.1)', {
      bottom: `calc(${scalePx(18)}px + env(safe-area-inset-bottom, 0px))`,
      left: `calc(${scalePx(22)}px + env(safe-area-inset-left, 0px))`,
      boxShadow: `0 0 10px ${WIRE_GREEN_DIM}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });

    const inner = document.createElement('span');
    Object.assign(inner.style, {
      color: WIRE_GREEN,
      fontSize: `${scalePx(10)}px`,
      letterSpacing: '1px',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);
    inner.textContent = t('touch.boost');
    this.boostBtn.appendChild(inner);
    this.boostBtnLabel = inner;

    this.root.appendChild(this.boostBtn);
  }

  /**
   * Sync the boost pill's label + glow to the current `_boostEnabled`
   * flag AND whether the pilot is actively pushing thrust. Three
   * visual states:
   *   - disarmed: dim green outline (same as disarmed fire).
   *   - armed + idle: cyan outline, label reads "ON" — boost is
   *     primed but not consuming anything because the ship isn't
   *     moving yet.
   *   - armed + thrusting: bright cyan glow — boost is actively
   *     engaged and costing energy, giving the pilot the same "I'm
   *     burning boost" feedback as holding the button used to.
   */
  private applyBoostButtonStyle(): void {
    if (!this._boostEnabled) {
      this.boostBtnLabel.textContent = t('touch.boost');
      this.boostBtnLabel.style.color = WIRE_GREEN;
      this.boostBtn.style.border = `2px solid ${WIRE_GREEN}`;
      this.boostBtn.style.boxShadow = `0 0 10px ${WIRE_GREEN_DIM}`;
      this.boostBtn.style.background = 'rgba(0,255,65,0.1)';
      return;
    }
    const cyan = '#33ccff';
    this.boostBtnLabel.textContent = t('touch.boostOn');
    this.boostBtnLabel.style.color = cyan;
    this.boostBtn.style.border = `2px solid ${cyan}`;
    if (this._lastThrusting) {
      this.boostBtn.style.boxShadow = `0 0 22px ${cyan}, inset 0 0 15px rgba(51,204,255,0.3)`;
      this.boostBtn.style.background = 'rgba(51,204,255,0.3)';
    } else {
      this.boostBtn.style.boxShadow = `0 0 10px rgba(51,204,255,0.6)`;
      this.boostBtn.style.background = 'rgba(51,204,255,0.12)';
    }
  }

  private buildDockButton(): void {
    const w = scalePx(96);
    const h = Math.max(scalePx(36), 36);
    this.dockBtn = document.createElement('div');
    Object.assign(this.dockBtn.style, {
      position: 'absolute',
      bottom: promptBottomCss(0),
      left: '50%',
      transform: 'translateX(-50%)',
      width: `${w}px`,
      height: `${h}px`,
      border: `2px solid ${WIRE_GREEN}`,
      background: WIRE_BG,
      boxShadow: `0 0 12px ${WIRE_GREEN_GLOW}`,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'auto',
      touchAction: 'none',
      borderRadius: '4px',
      color: WIRE_GREEN,
      fontSize: `${scalePx(11)}px`,
      letterSpacing: '2px',
    } as Partial<CSSStyleDeclaration>);
    this.dockBtn.textContent = t('touch.dock');
    this.root.appendChild(this.dockBtn);
  }

  private buildWarpButton(): void {
    const w = scalePx(84);
    const h = Math.max(scalePx(32), 32);
    this.warpBtn = document.createElement('div');
    Object.assign(this.warpBtn.style, {
      position: 'absolute',
      bottom: promptBottomCss(1),
      left: '50%',
      transform: 'translateX(-50%)',
      width: `${w}px`,
      height: `${h}px`,
      border: `2px solid #bb77ff`,
      background: WIRE_BG,
      boxShadow: `0 0 10px rgba(187,119,255,0.5)`,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'auto',
      touchAction: 'none',
      borderRadius: '4px',
      color: '#bb77ff',
      fontSize: `${scalePx(11)}px`,
      letterSpacing: '2px',
    } as Partial<CSSStyleDeclaration>);
    this.warpBtn.textContent = t('touch.warp');
    this.root.appendChild(this.warpBtn);
  }

  private buildTradeButton(): void {
    const w = scalePx(84);
    const h = Math.max(scalePx(32), 32);
    this.tradeBtn = document.createElement('div');
    Object.assign(this.tradeBtn.style, {
      position: 'absolute',
      bottom: promptBottomCss(1),
      left: '50%',
      transform: 'translateX(-50%)',
      width: `${w}px`,
      height: `${h}px`,
      border: `2px solid #ffb000`,
      background: WIRE_BG,
      boxShadow: `0 0 10px rgba(255,176,0,0.5)`,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'auto',
      touchAction: 'none',
      borderRadius: '4px',
      color: '#ffb000',
      fontSize: `${scalePx(11)}px`,
      letterSpacing: '2px',
    } as Partial<CSSStyleDeclaration>);
    this.tradeBtn.textContent = t('touch.trade');
    this.root.appendChild(this.tradeBtn);
  }

  /**
   * LAND / LIFTOFF proximity prompt. Shown when the pilot is in orbit
   * near a landable planet (label = LAND) or already on a surface near
   * a Spaceport (label = LIFTOFF). Color picked to match the desktop
   * HUD's `landPrompt` (#ffaa44, see hud.ts → buildLandPrompt) so a
   * player crossing devices recognises the affordance instantly. The
   * button stacks above the dock/warp/trade prompts when they happen
   * to be co-visible (extremely rare in practice — planet land radii
   * don't overlap station dock radii — but stacking keeps the UI
   * tidy if a future map ever puts both within range).
   */
  private buildLandButton(): void {
    const w = scalePx(96);
    const h = Math.max(scalePx(36), 36);
    const landColor = '#ffaa44';
    this.landBtn = document.createElement('div');
    Object.assign(this.landBtn.style, {
      position: 'absolute',
      bottom: promptBottomCss(0),
      left: '50%',
      transform: 'translateX(-50%)',
      width: `${w}px`,
      height: `${h}px`,
      border: `2px solid ${landColor}`,
      background: WIRE_BG,
      boxShadow: `0 0 12px rgba(255,170,68,0.5)`,
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'auto',
      touchAction: 'none',
      borderRadius: '4px',
      color: landColor,
      fontSize: `${scalePx(11)}px`,
      letterSpacing: '2px',
    } as Partial<CSSStyleDeclaration>);
    this.landBtn.textContent = t('touch.land');
    this.root.appendChild(this.landBtn);
  }

  /**
   * UP / DN (vertical thrust) buttons — the mobile equivalent of Space
   * and Ctrl on desktop. Placed as a vertical stack just to the right
   * of the left (thrust) joystick so the same thumb that drives
   * translation can reach them.
   */
  private buildVerticalThrustButtons(): void {
    const r = scalePx(28);
    const offsetLeft = scalePx(210);
    this.upBtn = this.makeCircle(r, `1px solid ${STICK_THUMB_BORDER}`, STICK_THUMB_BG, {
      bottom: `calc(${scalePx(195)}px + env(safe-area-inset-bottom, 0px))`,
      left: `calc(${offsetLeft}px + env(safe-area-inset-left, 0px))`,
      boxShadow: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background 0.1s',
    });
    const upLbl = document.createElement('span');
    Object.assign(upLbl.style, {
      color: STICK_LABEL_COLOR,
      fontSize: `${Math.max(10, scalePx(10))}px`,
      letterSpacing: '1px',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);
    upLbl.textContent = t('touch.up');
    this.upBtn.appendChild(upLbl);
    this.root.appendChild(this.upBtn);

    this.downBtn = this.makeCircle(r, `1px solid ${STICK_THUMB_BORDER}`, STICK_THUMB_BG, {
      bottom: `calc(${scalePx(90)}px + env(safe-area-inset-bottom, 0px))`,
      left: `calc(${offsetLeft}px + env(safe-area-inset-left, 0px))`,
      boxShadow: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background 0.1s',
    });
    const dnLbl = document.createElement('span');
    Object.assign(dnLbl.style, {
      color: STICK_LABEL_COLOR,
      fontSize: `${Math.max(10, scalePx(10))}px`,
      letterSpacing: '1px',
      pointerEvents: 'none',
    } as Partial<CSSStyleDeclaration>);
    dnLbl.textContent = t('touch.down');
    this.downBtn.appendChild(dnLbl);
    this.root.appendChild(this.downBtn);
  }

  /**
   * Top-center icon launcher bar. Each entry is a square icon button
   * (replacing the previous three-letter text pills) so the bar reads
   * as a glanceable HUD launcher rather than a row of micro-text.
   * `aria-label` / `title` carry the full localized name so the icons
   * stay discoverable for accessibility.
   */
  private buildTopButtonBar(): void {
    const bar = document.createElement('div');
    Object.assign(bar.style, {
      position: 'absolute',
      top: `calc(${scalePx(12)}px + env(safe-area-inset-top, 0px))`,
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: `${Math.max(8, scalePx(10))}px`,
      pointerEvents: 'none',
      zIndex: '1',
    } as Partial<CSSStyleDeclaration>);

    // makeIconButton builds with `position: absolute` (designed for
    // corner-anchored prompt buttons); rebase to normal flow so the
    // flex bar can space them out reliably.
    const mkBarBtn = (icon: string, label: string): HTMLElement => {
      const btn = this.makeIconButton(icon, label);
      btn.style.position = 'relative';
      btn.style.top = '';
      btn.style.right = '';
      btn.style.left = '';
      btn.style.bottom = '';
      return btn;
    };

    // Inline-SVG icons (see ICON_* constants near the top of the
    // file for each glyph + rationale). The previous Unicode-glyph
    // set rendered inconsistently across mobile platforms — the
    // inventory grid in particular was "barely visible" on Android
    // — and several glyphs carried no semantic link to what the
    // button actually opened (a sparkle for missions, a smiley for
    // friends). The new set replaces those with line-art icons:
    //   - Menu:      hamburger (unchanged affordance, sharper render).
    //   - Map:       globe (matches the universe-map metaphor).
    //   - Missions:  clipboard with check (universal "objectives").
    //   - Inventory: satchel/backpack (clear "carry bag" silhouette).
    //   - Friends:   two-people "users" icon.
    this.menuBtn       = mkBarBtn(ICON_MENU,      t('touch.menu'));
    this.mapBtn        = mkBarBtn(ICON_GLOBE,     t('touch.map'));
    this.missionsBtn   = mkBarBtn(ICON_CLIPBOARD, t('touch.missions'));
    this.inventoryBtn  = mkBarBtn(ICON_BACKPACK,  t('touch.inventory'));
    this.friendsBtn    = mkBarBtn(ICON_USERS,     t('touch.friends'));

    // CHAT / USE / WEAPON-CYCLE pills have been retired from the top
    // bar. CHAT: redundant — the chat panel itself is tappable on
    // mobile. USE: the desktop "quick-use first consumable" flow has
    // no clear touch affordance; the INVENTORY panel's CONSUMABLES tab
    // now owns one-tap consumable usage. WEAPON-CYCLE: had no visible
    // effect in game. The class still references these handles in a
    // few places (hitTest / visibility toggles), so keep detached,
    // display:none stubs rather than null out the fields.
    this.chatBtn = document.createElement('div');
    this.chatBtn.style.display = 'none';
    this.useBtn = document.createElement('div');
    this.useBtn.style.display = 'none';
    this.weaponCycleBtn = document.createElement('div');
    this.weaponCycleBtn.style.display = 'none';

    bar.append(
      this.menuBtn,
      this.mapBtn,
      this.missionsBtn,
      this.inventoryBtn,
      this.friendsBtn,
    );
    this.topBar = bar;
    this.root.appendChild(bar);
  }

  private flashBtn(el: HTMLElement, baseBg: string): void {
    el.style.background = 'rgba(0,255,65,0.3)';
    setTimeout(() => {
      if (!this.disposed) el.style.background = baseBg;
    }, 150);
  }

  private handleTouchStart(e: TouchEvent): void {
    e.preventDefault();

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;
      const tx = touch.clientX;
      const ty = touch.clientY;

      // Tap-only buttons get resolved first so they never get swallowed
      // by the joystick hit tests.
      if (this.hitTest(this.menuBtn, tx, ty)) {
        this.cb.onMenu?.();
        this.flashBtn(this.menuBtn, WIRE_BG);
        continue;
      }
      if (this.hitTest(this.mapBtn, tx, ty)) {
        this.cb.onMap?.();
        this.flashBtn(this.mapBtn, WIRE_BG);
        continue;
      }
      if (this.hitTest(this.missionsBtn, tx, ty)) {
        this.cb.onMissions?.();
        this.flashBtn(this.missionsBtn, WIRE_BG);
        continue;
      }
      if (this.hitTest(this.inventoryBtn, tx, ty)) {
        this.cb.onInventory?.();
        this.flashBtn(this.inventoryBtn, WIRE_BG);
        continue;
      }
      if (this.hitTest(this.friendsBtn, tx, ty)) {
        this.cb.onFriends?.();
        this.flashBtn(this.friendsBtn, WIRE_BG);
        continue;
      }
      if (this.hitTest(this.chatBtn, tx, ty)) {
        this.cb.onChat?.();
        this.flashBtn(this.chatBtn, WIRE_BG);
        continue;
      }
      if (this.hitTest(this.useBtn, tx, ty)) {
        this.cb.onUse?.();
        this.flashBtn(this.useBtn, WIRE_BG);
        continue;
      }

      if (this.hitTest(this.fireBtn, tx, ty)) {
        // Toggle semantics: one tap flips auto-fire on/off. We don't
        // track a fireIdentifier anymore — the toggle state outlives
        // the touch that produced it. `main.ts` decides each frame
        // whether a valid target is in range and sets
        // `_autoFireActive`; that's what actually drives `isFiring()`.
        this._autoFireEnabled = !this._autoFireEnabled;
        if (!this._autoFireEnabled) {
          this._autoFireActive = false;
        }
        this.applyFireButtonStyle();
        this.cb.onToggleAutoFire?.(this._autoFireEnabled);
        continue;
      }

      if (this.hitTest(this.boostBtn, tx, ty)) {
        // Toggle semantics (same pattern as fire): one tap flips the
        // boost armed state. Actual boosting (= `isBoosting()` → true)
        // still requires the pilot to be pushing thrust this frame,
        // so idle + armed = no energy burn, no server-side change.
        this._boostEnabled = !this._boostEnabled;
        this.applyBoostButtonStyle();
        this.cb.onToggleBoost?.(this._boostEnabled);
        continue;
      }

      if (this.hitTest(this.upBtn, tx, ty)) {
        this._upHeld = true;
        this.upIdentifier = touch.identifier;
        this.upBtn.style.background = STICK_THUMB_BG_ACTIVE;
        continue;
      }

      if (this.hitTest(this.downBtn, tx, ty)) {
        this._downHeld = true;
        this.downIdentifier = touch.identifier;
        this.downBtn.style.background = STICK_THUMB_BG_ACTIVE;
        continue;
      }

      if (this.dockVisible && this.hitTest(this.dockBtn, tx, ty)) {
        this.cb.onDock?.();
        continue;
      }

      if (this.warpVisible && this.hitTest(this.warpBtn, tx, ty)) {
        this.cb.onWarp?.();
        continue;
      }

      if (this.tradeVisible && this.hitTest(this.tradeBtn, tx, ty)) {
        this.cb.onTrade?.();
        continue;
      }

      if (this.landVisible && this.hitTest(this.landBtn, tx, ty)) {
        this.cb.onLand?.();
        continue;
      }

      if (this.hitTest(this.weaponCycleBtn, tx, ty)) {
        this.cb.onWeaponCycle?.();
        this.flashBtn(this.weaponCycleBtn, WIRE_BG);
        continue;
      }

      if (this.hitTest(this.leftBase, tx, ty) && !this.leftJoystick.active) {
        this.leftJoystick.active = true;
        this.leftJoystick.identifier = touch.identifier;
        const rect = this.leftBase.getBoundingClientRect();
        this.leftJoystick.originX = rect.left + rect.width / 2;
        this.leftJoystick.originY = rect.top + rect.height / 2;
        this.leftJoystick.currentX = tx;
        this.leftJoystick.currentY = ty;
        continue;
      }

      if (this.hitTest(this.rightBase, tx, ty) && !this.rightJoystick.active) {
        this.rightJoystick.active = true;
        this.rightJoystick.identifier = touch.identifier;
        const rect = this.rightBase.getBoundingClientRect();
        this.rightJoystick.originX = rect.left + rect.width / 2;
        this.rightJoystick.originY = rect.top + rect.height / 2;
        this.rightJoystick.currentX = tx;
        this.rightJoystick.currentY = ty;
        continue;
      }

      // Anywhere else: treat a drag-start on the left half of the screen
      // as a "virtual joystick spawn" so players don't have to hunt for
      // the base. This mirrors how many mobile twin-stick games behave.
      const halfX = window.innerWidth / 2;
      if (tx < halfX && !this.leftJoystick.active) {
        this.leftJoystick.active = true;
        this.leftJoystick.identifier = touch.identifier;
        this.leftJoystick.originX = tx;
        this.leftJoystick.originY = ty;
        this.leftJoystick.currentX = tx;
        this.leftJoystick.currentY = ty;
      } else if (tx >= halfX && !this.rightJoystick.active) {
        this.rightJoystick.active = true;
        this.rightJoystick.identifier = touch.identifier;
        this.rightJoystick.originX = tx;
        this.rightJoystick.originY = ty;
        this.rightJoystick.currentX = tx;
        this.rightJoystick.currentY = ty;
      }
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    e.preventDefault();

    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;

      if (touch.identifier === this.leftJoystick.identifier) {
        this.leftJoystick.currentX = touch.clientX;
        this.leftJoystick.currentY = touch.clientY;
      }

      if (touch.identifier === this.rightJoystick.identifier) {
        this.rightJoystick.currentX = touch.clientX;
        this.rightJoystick.currentY = touch.clientY;
      }
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i]!;

      if (touch.identifier === this.leftJoystick.identifier) {
        this.leftJoystick.active = false;
        this.leftJoystick.identifier = -1;
        this.leftJoystick.dx = 0;
        this.leftJoystick.dy = 0;
        this.resetThumb(this.leftThumb, this.leftBase);
      }

      if (touch.identifier === this.rightJoystick.identifier) {
        this.rightJoystick.active = false;
        this.rightJoystick.identifier = -1;
        this.rightJoystick.dx = 0;
        this.rightJoystick.dy = 0;
        this.resetThumb(this.rightThumb, this.rightBase);
      }

      // Fire is a toggle now (see handleTouchStart); there's nothing
      // to release on touchend. The auto-fire visual state persists
      // across individual taps and is only cleared by another tap, by
      // hide(), or by the pilot losing the target in-game.

      // Boost is a toggle now (see handleTouchStart); nothing to
      // release on touchend. Armed state persists until the pilot
      // taps the pill again or the overlay is hidden via hide().

      if (touch.identifier === this.upIdentifier) {
        this._upHeld = false;
        this.upIdentifier = -1;
        this.upBtn.style.background = STICK_THUMB_BG;
      }

      if (touch.identifier === this.downIdentifier) {
        this._downHeld = false;
        this.downIdentifier = -1;
        this.downBtn.style.background = STICK_THUMB_BG;
      }
    }
  }

  private handleResize(): void {
    // elements use scalePx at build time; full rebuild on big changes is overkill
    // but we can update joystick positions if needed
  }

  private hitTest(el: HTMLElement, tx: number, ty: number): boolean {
    if (!el || el.style.display === 'none') return false;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    // Inflate the hit box a little for nav pills so thumbs don't miss.
    const pad = 6;
    return tx >= rect.left - pad && tx <= rect.right + pad && ty >= rect.top - pad && ty <= rect.bottom + pad;
  }

  private resetThumb(thumb: HTMLElement, base: HTMLElement): void {
    const baseR = base.offsetWidth / 2;
    const thumbR = thumb.offsetWidth / 2;
    thumb.style.left = `${baseR - thumbR}px`;
    thumb.style.top = `${baseR - thumbR}px`;
  }

  private computeJoystick(js: JoystickState): { x: number; y: number } {
    if (!js.active) return { x: 0, y: 0 };

    const dx = js.currentX - js.originX;
    const dy = js.currentY - js.originY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxOff = scalePx(JOYSTICK_MAX_OFFSET);
    const clamped = Math.min(dist, maxOff);

    if (dist < 1) return { x: 0, y: 0 };

    const nx = (dx / dist) * (clamped / maxOff);
    const ny = (dy / dist) * (clamped / maxOff);

    // Radial deadzone with smooth re-mapping above the threshold. The
    // old per-axis hard cutoff (`Math.abs(nx) < DEADZONE ? 0 : nx`)
    // had two problems: (1) the boundary was a discontinuous jump
    // from 0 → ±0.12, which players felt as "the stick suddenly
    // grabs", and (2) crossing the diagonal could leave one axis
    // dead while the other was live, biasing fine aim toward the
    // cardinal directions. Switching to a magnitude-based deadzone
    // and re-scaling [DEADZONE..1] → [0..1] removes both: the stick
    // is silent under tiny finger jitter, then ramps up linearly
    // from the deadzone edge so even the first 5% of usable travel
    // produces a responsive output instead of being absorbed.
    const mag = Math.hypot(nx, ny);
    if (mag < DEADZONE) return { x: 0, y: 0 };
    const live = (mag - DEADZONE) / (1 - DEADZONE);
    const k = live / mag;
    return { x: nx * k, y: ny * k };
  }

  update(): void {
    if (this.disposed || !this.visible) return;

    const left = this.computeJoystick(this.leftJoystick);
    this.leftJoystick.dx = left.x;
    this.leftJoystick.dy = left.y;
    this.updateThumbPosition(this.leftThumb, this.leftBase, left.x, left.y);
    this.leftThumb.style.background = this.leftJoystick.active
      ? STICK_THUMB_BG_ACTIVE
      : STICK_THUMB_BG;

    const right = this.computeJoystick(this.rightJoystick);
    this.rightJoystick.dx = right.x;
    this.rightJoystick.dy = right.y;
    this.updateThumbPosition(this.rightThumb, this.rightBase, right.x, right.y);
    this.rightThumb.style.background = this.rightJoystick.active
      ? STICK_THUMB_BG_ACTIVE
      : STICK_THUMB_BG;
  }

  private updateThumbPosition(
    thumb: HTMLElement,
    base: HTMLElement,
    nx: number,
    ny: number,
  ): void {
    const baseR = base.offsetWidth / 2;
    const thumbR = thumb.offsetWidth / 2;
    const maxOff = scalePx(JOYSTICK_MAX_OFFSET);
    const ox = nx * maxOff;
    const oy = ny * maxOff;
    thumb.style.left = `${baseR - thumbR + ox}px`;
    thumb.style.top = `${baseR - thumbR + oy}px`;
  }

  getThrust(): { x: number; y: number; z: number } {
    let y = 0;
    if (this._upHeld) y += 1;
    if (this._downHeld) y -= 1;
    return { x: this.leftJoystick.dx, y, z: -this.leftJoystick.dy };
  }

  getRotation(): { x: number; y: number } {
    // Apply a mild response curve to the right stick's magnitude
    // (direction preserved). The raw deflection is linear in [-1, 1],
    // which feels twitchy at the low end. We previously used a strict
    // quadratic curve (mag²) — that turned a 30% push into 9% rate
    // and felt laggy on iOS because the stick had to be deflected
    // 70%+ before anything visible happened. mag^1.4 keeps the
    // tip-of-finger precision at the very low end (5% push ≈ 1.6%
    // rate, deadzone-friendly) but lets a half push give ~38% rate
    // for natural, immediate response. Full deflection still hits
    // 100% so the input-manager's `TOUCH_LOOK_RATE` is still the
    // true max. The `* 2` preserves the pre-existing input-manager
    // convention (`touchAim = overlayRotation * -0.5`), so the
    // effective deflection passed downstream is still in [-1, 1].
    const dx = this.rightJoystick.dx;
    const dy = this.rightJoystick.dy;
    const mag = Math.hypot(dx, dy);
    if (mag < 1e-4) return { x: 0, y: 0 };
    const curvedMag = Math.pow(mag, 1.4);
    const scale = curvedMag / mag;
    return { x: -dy * scale * 2, y: -dx * scale * 2 };
  }

  isFiring(): boolean {
    // Fire input is only true when the pilot has explicitly armed
    // auto-fire AND `main.ts` has decided this frame's crosshair target
    // is valid + in range. That gating keeps us from spamming shots
    // while aiming at empty space or out-of-range objects, which would
    // drain energy and pull aggro for no reason.
    return this._autoFireEnabled && this._autoFireActive;
  }

  isBoosting(): boolean {
    // Boost is gated on actual thrust input: leaving boost armed in
    // an idle drift doesn't consume anything server-side. We also
    // piggy-back the visual refresh here so the pill's "armed vs
    // burning" glow stays in sync with what the input-manager is
    // about to send to the server this frame.
    const thrusting = this.hasThrustInput();
    if (thrusting !== this._lastThrusting) {
      this._lastThrusting = thrusting;
      if (this._boostEnabled) {
        this.applyBoostButtonStyle();
      }
    }
    return this._boostEnabled && thrusting;
  }

  isAutoFireEnabled(): boolean {
    return this._autoFireEnabled;
  }

  isBoostEnabled(): boolean {
    return this._boostEnabled;
  }

  /**
   * "Is the pilot actively commanding any thrust direction this
   * frame?" — covers the left stick's planar push AND the UP/DN
   * vertical pills. We treat "moving" as "pushing the throttle",
   * not "ship has nonzero velocity", because velocity can be nonzero
   * from drift / external pushes where boosting would be a waste.
   */
  private hasThrustInput(): boolean {
    if (this._upHeld || this._downHeld) return true;
    if (this.leftJoystick.dx !== 0 || this.leftJoystick.dy !== 0) return true;
    return false;
  }

  /**
   * Called each frame by `main.ts` once it has evaluated the crosshair
   * target against the equipped weapon's range. Only meaningful while
   * `_autoFireEnabled` is true — when the pilot hasn't armed auto-fire
   * the flag is ignored anyway.
   */
  setAutoFireActive(active: boolean): void {
    this._autoFireActive = active;
  }

  showDock(visible: boolean): void {
    this.dockVisible = visible;
    this.dockBtn.style.display = visible ? 'flex' : 'none';
  }

  /**
   * Hot-swap the DOCK button's label (used when the pilot docks at a
   * station so the button becomes UNDOCK). The action callback is
   * single-purpose and set by `main.ts` via `setCallbacks`.
   */
  setDockLabel(label: string): void {
    this.dockBtn.textContent = label;
  }

  showWarp(visible: boolean): void {
    this.warpVisible = visible;
    // Warp button stacks on top of the dock button if both prompts happen
    // to be visible in the same frame — push it higher when that happens.
    this.warpBtn.style.display = visible ? 'flex' : 'none';
    this.warpBtn.style.bottom = promptBottomCss(this.dockVisible ? 1 : 0);
  }

  showTrade(visible: boolean): void {
    this.tradeVisible = visible;
    this.tradeBtn.style.display = visible ? 'flex' : 'none';
    // Stack: dock (lowest) -> warp -> trade.
    const stacked =
      (this.dockVisible ? 1 : 0) + (this.warpVisible ? 1 : 0);
    this.tradeBtn.style.bottom = promptBottomCss(stacked);
  }

  /**
   * Toggle the LAND / LIFTOFF proximity prompt. Visibility is driven
   * by `main.ts` every frame from the HUD's nearbyPlanetId / nearSpaceport
   * state; the label is set independently via {@link setLandLabel}
   * so the same button can present as LAND in orbit or LIFTOFF on
   * the surface without us recreating the DOM node. Stacks above the
   * dock/warp/trade prompts when they coexist with it (rare in
   * practice but cheap to guard against).
   */
  showLand(visible: boolean): void {
    this.landVisible = visible;
    this.landBtn.style.display = visible ? 'flex' : 'none';
    const stacked =
      (this.dockVisible ? 1 : 0) +
      (this.warpVisible ? 1 : 0) +
      (this.tradeVisible ? 1 : 0);
    this.landBtn.style.bottom = promptBottomCss(stacked);
  }

  /**
   * Swap the LAND button's label (used to flip between LAND in orbit
   * and LIFTOFF on a surface). The action callback is a single
   * `onLand` slot — see TouchControlsCallbacks; `main.ts` decides
   * which network message to send based on `onSurface`.
   */
  setLandLabel(label: string): void {
    this.landBtn.textContent = label;
  }

  /**
   * Toggle the gameplay overlay (fire/boost/up/dn + the flight joysticks
   * + the entire MENU / MAP / INV top-bar pill row). When any in-game
   * window is up (inventory, map, warp UI, pause menu, merchant,
   * controls help, …) every mobile HUD control is hidden so it doesn't
   * overlap the modal — windows own their own ✕ close button to get
   * out. The DOCK / WARP / TRADE / LAND proximity prompt buttons
   * remain tied to their own `showX(visible)` flags (e.g. DOCK stays
   * visible while docked so it can act as the UNDOCK button; LAND
   * stays visible while on a surface so it can act as LIFTOFF).
   * CHAT / USE / WEAPON-CYCLE are detached stubs (see
   * buildTopButtonBar) and intentionally never toggled visible.
   */
  setGameplayButtonsVisible(visible: boolean): void {
    const d = visible ? 'flex' : 'none';
    this.fireBtn.style.display = d;
    this.boostBtn.style.display = d;
    this.upBtn.style.display = d;
    this.downBtn.style.display = d;
    this.leftBase.style.display = d;
    this.rightBase.style.display = d;
    this.menuBtn.style.display = d;
    this.inventoryBtn.style.display = d;
    this.mapBtn.style.display = d;
    this.missionsBtn.style.display = d;
    this.friendsBtn.style.display = d;
    // Prompt buttons follow their own visibility flags regardless of the
    // overall gameplay toggle — see showDock/showWarp/showTrade.
  }

  show(): void {
    this.visible = true;
    this.root.style.display = 'block';
  }

  hide(): void {
    this.visible = false;
    this.root.style.display = 'none';
    this.leftJoystick.active = false;
    this.rightJoystick.active = false;
    // Disarm auto-fire when the overlay is hidden (pilot docked,
    // logged out, etc.) so they don't come back to a surprise trigger
    // locked on. We don't fire `onToggleAutoFire` from here — the HUD
    // is already gone, so a notification would be noise.
    if (this._autoFireEnabled) {
      this._autoFireEnabled = false;
      this.applyFireButtonStyle();
    }
    this._autoFireActive = false;
    // Same reasoning as auto-fire: disarm boost on overlay hide so
    // re-entry doesn't silently resume burning energy next time the
    // pilot nudges the stick.
    if (this._boostEnabled) {
      this._boostEnabled = false;
      this._lastThrusting = false;
      this.applyBoostButtonStyle();
    }
    this._upHeld = false;
    this._downHeld = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.root.removeEventListener('touchstart', this.onTouchStart);
    this.root.removeEventListener('touchmove', this.onTouchMove);
    this.root.removeEventListener('touchend', this.onTouchEnd);
    this.root.removeEventListener('touchcancel', this.onTouchEnd);
    window.removeEventListener('resize', this.onResize);

    this.root.remove();
  }
}
